import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTournamentForecast, type ModelForecast, type UserExpectedScore } from '../api';

function pct(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (v < 0.001) return '<0.1%';
  return `${(v * 100).toFixed(1)}%`;
}

export default function ForecastPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['tournament-forecast'],
    queryFn: getTournamentForecast,
    refetchInterval: 10 * 60_000,
  });
  if (isLoading) return <section className="card p-4 text-xs text-dim">Loading forecasts…</section>;
  if (!data) return null;
  const models = data.models.filter((m) => m.teams.length > 0);
  if (models.length === 0) return null;

  return (
    <div className="space-y-4">
      <ExpectedScoreTable scores={data.scores} models={models} />
      <ModelsCompare models={models} />
    </div>
  );
}

function ExpectedScoreTable({ scores, models }: { scores: UserExpectedScore[]; models: ModelForecast[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-xs uppercase tracking-widest text-dim">Expected score · pre-tournament</h2>
        <span className="text-[10px] text-dim">{models.length} models · 10pt champ / 5pt RU</span>
      </div>
      <ol className="space-y-1.5">
        {scores.map((s, i) => {
          const isOpen = open === s.user;
          return (
            <li key={s.user}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : s.user)}
                className="w-full flex items-center gap-2 text-sm text-left hover:bg-ink-800/40 rounded-md px-1 py-1"
              >
                <span className="w-4 text-right font-mono text-dim text-[10px]">{i + 1}</span>
                <span className="flex-1 font-semibold truncate">{s.user}</span>
                <span className="text-[10px] text-dim hidden sm:inline truncate max-w-[180px]">
                  {s.champion ?? '—'} 🏆 · {s.runner_up ?? '—'} 🥈
                </span>
                <span className="stat tabular-nums w-14 text-right text-pitch-400">{s.aggregate.toFixed(2)}</span>
                <span className="text-dim text-[10px] w-3 text-center">{isOpen ? '▾' : '▸'}</span>
              </button>
              {isOpen && (
                <div className="mt-1 mb-2 ml-6 mr-1 rounded-lg bg-ink-800/40 p-2.5 space-y-1.5">
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 text-[10px] text-dim uppercase tracking-wider pb-1 border-b border-ink-700">
                    <span>Model</span>
                    <span className="text-right">Champ p</span>
                    <span className="text-right">RU p</span>
                    <span className="text-right">Exp pts</span>
                  </div>
                  {s.perModel.map((pm) => {
                    const m = models.find((x) => x.id === pm.id);
                    if (!m) return null;
                    return (
                      <div key={pm.id} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 text-xs">
                        <span className="text-dim">{m.label}</span>
                        <span className="text-right font-mono">{pct(pm.cupChance)}</span>
                        <span className="text-right font-mono text-cyan-400/80">{pct(pm.runnerUpChance)}</span>
                        <span className="text-right font-mono text-pitch-400">{pm.expected.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <p className="text-[10px] text-dim mt-2">Tap a name to see per-model breakdown. Score = avg over models of (P(champion) × 10 + P(runner-up) × 5).</p>
    </section>
  );
}

function ModelsCompare({ models }: { models: ModelForecast[] }) {
  return (
    <section className="card p-4">
      <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Top contenders per model</h2>
      <div className={`grid grid-cols-1 ${models.length >= 3 ? 'lg:grid-cols-3' : 'sm:grid-cols-2'} gap-3`}>
        {models.map((m) => (
          <div key={m.id}>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-sm font-semibold">{m.label}</div>
              {m.note && <div className="text-[10px] text-dim truncate">{m.note}</div>}
            </div>
            <ul className="space-y-0.5">
              {m.teams.slice(0, 8).map((t, i) => (
                <li key={t.code} className="flex items-center gap-2 text-xs">
                  <span className="w-4 text-right font-mono text-dim text-[10px]">{i + 1}</span>
                  <span className="flex-1 truncate">{t.flag} {t.name}</span>
                  <span className="font-mono text-pitch-400">{pct(t.cupChance)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
